import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { AskEvoAnswerView } from './AskEvoAnswerView.js';
import { parseAskEvo } from './askEvoResult.js';
import { BriefCreatedStrip, BriefRows, briefed } from './BriefCard.js';
import { ComboCard } from './ComboCard.js';
import { type CreativeDetail, CreativeDetailOpener } from './CreativeDetailOpener.js';
import { CreativeRows } from './CreativeRows.js';
import { CreativeScoresView } from './CreativeScoresView.js';
import { comboKeysOf, combosOf, elementCountsOf } from './comboRows.js';
import { ComparisonView } from './comparison/ComparisonView.js';
import { comparisonResultSchema } from './comparison/comparisonResult.js';
import { SceneLegend } from './ElementTag.js';
import { EmptyResultState } from './EmptyResultState.js';
import { emptyRunOf } from './emptyResult.js';
import { GemGroupSection, type GemPicks } from './GemCard.js';
import { gemGroupsOf, gemKeysOf } from './gemRows.js';
import { BannerStoryboardView } from './ideation/banner/BannerStoryboardView.js';
import { GeneratedBannerGallery } from './ideation/banner/GeneratedBannerGallery.js';
import { type ElementSelection, ideationResultSchema } from './ideation/brief/briefResult.js';
import { IdeationBriefCard } from './ideation/brief/IdeationBriefCard.js';
import { ProvenElementsView } from './ideation/ProvenElementsView.js';
import { StoryboardView } from './ideation/storyboard/StoryboardView.js';
import { GeneratedVideoGallery } from './ideation/video/GeneratedVideoGallery.js';
import { CHECKBOX_SX } from './Pill.js';
import { type ParsedResult, parseResult } from './parsedResult.js';
import {
  RecommendationCard,
  RecommendationFailure,
  RecommendationScores,
} from './RecommendationCard.js';
import { ReportView } from './ReportView.js';
import { RisingElementsView } from './RisingElementCard.js';
import { isIterationsResult, outcomeOfRow } from './recommendationRows.js';
import { parseReport } from './reportResult.js';
import { columnsOf, type ResultRow, summaryOf } from './resultRows.js';
import { text } from './resultValues.js';
import { risingGroupsOf } from './risingRows.js';
import { ScoredBatchView } from './ScoredBatchView.js';
import { isCreativeScoresResult } from './scoreRows.js';
import type { EmptyState, ResultSelection, WorkflowRun, WorkflowStep } from './workflows.types.js';

interface Picks {
  picked: Record<string, number[]>;
  onToggle: (creativeId: string, recommendationId: number) => void;
  onToggleAll: (creativeId: string, all: number[]) => void;
}

interface ResultViewProps {
  run: WorkflowRun;
  resultData?: ParsedResult;
  selection?: ResultSelection;
  picks?: Picks;
  // An ideation brief is picked element by element, not as a whole.
  elementSelection?: ElementSelection;
  // A gem or a combination is picked by its own row key, not by a creative.
  gemPicks?: GemPicks & { onToggleAll: (keys: string[]) => void };
  // A comparison's tags, picked by their row key to chain on as elements.
  elementPicks?: GemPicks;
  // Absent wherever the brief is already open, which is why the drawer's own
  // brief view offers no way to open it again. Takes the creative whose brief
  // was asked for: a run holds one per creative.
  onViewBrief?: (creativeId: string) => void;
  onOpenFullAnalysis?: () => void;
  onOpenCreative?: (creativeId: string, networkId: number) => void;
  creativeDetail?: CreativeDetail;
  // A completed run with no rows, off the result step; the config step tells
  // which preset the run used.
  emptyState?: EmptyState | null;
  configStep?: WorkflowStep;
  onEditConfig?: () => void;
  // The workflow page folds each recommendation's reasoning away; the drawer shows it in full.
  collapsibleWhy?: boolean;
}

function SelectAllHeader({
  picks,
  keys,
  children,
}: {
  picks: ResultViewProps['gemPicks'];
  keys: string[];
  children: ReactNode;
}): ReactNode {
  return (
    <Stack
      direction="row"
      sx={{ px: 1, gap: 2, alignItems: 'center', justifyContent: 'space-between' }}
    >
      {picks === undefined ? (
        <Box />
      ) : (
        <Stack
          component="label"
          direction="row"
          sx={{ gap: 1, alignItems: 'center', cursor: 'pointer' }}
        >
          <Checkbox
            size="small"
            checked={picks.picked.length === keys.length}
            indeterminate={picks.picked.length > 0 && picks.picked.length < keys.length}
            onChange={() => picks.onToggleAll(keys)}
            sx={CHECKBOX_SX}
            slotProps={{ input: { 'aria-label': 'Select all' } }}
          />
          <Typography sx={{ fontSize: 13 }}>Select all</Typography>
        </Stack>
      )}
      {children}
    </Stack>
  );
}

function groupLabelOf(row: ResultRow): string | null {
  return text(row.values['group_label']);
}

// Rows the service split into named groups (Spend Misallocation), each group kept together in the
// order it first appears; null when no row names a group.
function groupedRowsOf(rows: ResultRow[]): ResultRow[] | null {
  const labels = [...new Set(rows.map(groupLabelOf))];
  if (labels.every((label) => label === null)) return null;
  return labels.flatMap((label) => rows.filter((row) => groupLabelOf(row) === label));
}

function iterationsDetail(
  row: ResultRow,
  picks: Picks | undefined,
  collapsibleWhy: boolean,
): ReactNode {
  const outcome = outcomeOfRow(row);
  if (outcome.failed) return <RecommendationFailure error={outcome.error} />;
  if (outcome.recommendations.length === 0) return null;
  // Only a recommendation the service gave an id can be picked: the id is what
  // the brief is asked for, so one without it is readable but not briefable.
  const pickable = outcome.recommendations.flatMap((recommendation) => recommendation.id ?? []);
  const chosen = (row.creativeId === null ? undefined : picks?.picked[row.creativeId]) ?? [];
  const pickable_ = row.creativeId === null ? [] : pickable;

  return (
    <>
      <RecommendationScores outcome={outcome} />
      {picks === undefined || pickable_.length === 0 ? null : (
        <Stack
          direction="row"
          sx={{
            px: 4,
            pb: 2,
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Stack
            component="label"
            direction="row"
            sx={{ gap: 1.5, alignItems: 'center', cursor: 'pointer' }}
          >
            <Checkbox
              checked={chosen.length === pickable_.length}
              indeterminate={chosen.length > 0 && chosen.length < pickable_.length}
              onChange={() => picks.onToggleAll(row.creativeId as string, pickable_)}
              size="small"
              sx={CHECKBOX_SX}
              slotProps={{ input: { 'aria-label': `Select all for ${row.label}` } }}
            />
            <Typography sx={{ fontSize: 12 }}>Select all</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
            {outcome.recommendations.length} recommendation
            {outcome.recommendations.length === 1 ? '' : 's'} — pick what to brief
          </Typography>
        </Stack>
      )}
      {outcome.recommendations.map((recommendation) => (
        <RecommendationCard
          key={recommendation.key}
          recommendation={recommendation}
          collapsibleWhy={collapsibleWhy}
          {...(picks === undefined || recommendation.id === null || row.creativeId === null
            ? {}
            : {
                pick: {
                  checked: chosen.includes(recommendation.id),
                  onToggle: () =>
                    picks.onToggle(row.creativeId as string, recommendation.id as number),
                },
              })}
        />
      ))}
    </>
  );
}

export function ResultView({
  run,
  resultData = parseResult(run.result, run.resultKind),
  selection,
  picks,
  collapsibleWhy = false,
  elementSelection,
  gemPicks,
  elementPicks,
  onViewBrief,
  onOpenFullAnalysis,
  onOpenCreative,
  creativeDetail,
  emptyState = null,
  configStep,
  onEditConfig,
}: ResultViewProps): ReactNode {
  if (resultData.kind === 'ideation_brief') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="error">This ideation brief could not be read.</Alert>;
    if (parsed.data.briefs.length === 0)
      return <Alert severity="info">No ideation briefs were returned.</Alert>;
    return (
      <Stack sx={{ gap: 3 }}>
        {parsed.data.briefs.map((brief) => (
          <IdeationBriefCard
            key={brief.name}
            brief={brief}
            {...(elementSelection === undefined ? {} : { selection: elementSelection })}
          />
        ))}
      </Stack>
    );
  }
  if (resultData.kind === 'storyboard') {
    const { parsed } = resultData;
    if (!parsed.success) return <Alert severity="error">This storyboard could not be read.</Alert>;
    if (parsed.data.versions.length === 0)
      return <Alert severity="info">No storyboard versions were returned.</Alert>;
    return (
      <StoryboardView result={parsed.data} {...(selection === undefined ? {} : { selection })} />
    );
  }
  if (resultData.kind === 'video') {
    const { parsed } = resultData;
    if (!parsed.success) return <Alert severity="error">These videos could not be read.</Alert>;
    if (parsed.data.videos.length === 0)
      return <Alert severity="info">No videos were returned.</Alert>;
    return <GeneratedVideoGallery result={parsed.data} />;
  }
  if (run.resultKind === 'comparison' && resultData.kind === 'other') {
    const parsed = comparisonResultSchema.safeParse(run.result);
    if (!parsed.success)
      return <Alert severity="warning">This comparison could not be read.</Alert>;
    const renderComparison = (onOpen?: (row: ResultRow) => void): ReactNode => (
      <ComparisonView
        result={parsed.data}
        pool={resultData.rows}
        labels={run.labels ?? {}}
        {...(selection === undefined ? {} : { selection })}
        {...(elementPicks === undefined ? {} : { elementPicks })}
        {...(onOpen === undefined ? {} : { onOpen })}
      />
    );
    return creativeDetail === undefined ? (
      renderComparison()
    ) : (
      <CreativeDetailOpener
        workflowId={run.actionType}
        rows={resultData.rows}
        creativeDetail={creativeDetail}
      >
        {renderComparison}
      </CreativeDetailOpener>
    );
  }
  if (resultData.kind === 'banner_storyboard') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="warning">This banner storyboard could not be read.</Alert>;
    if (parsed.data.versions.length === 0)
      return <Alert severity="info">No banner versions were returned.</Alert>;
    return (
      <BannerStoryboardView
        result={parsed.data}
        {...(selection === undefined ? {} : { selection })}
      />
    );
  }
  if (resultData.kind === 'banner') {
    const { parsed } = resultData;
    if (!parsed.success) return <Alert severity="warning">These banners could not be read.</Alert>;
    if (parsed.data.banners.length === 0)
      return <Alert severity="info">No banners were returned.</Alert>;
    return <GeneratedBannerGallery result={parsed.data} />;
  }
  if (run.resultKind === 'creative_list' && resultData.kind === 'other') {
    const { rows } = resultData;
    if (rows.length === 0) {
      const emptyRun =
        emptyState === null ? null : emptyRunOf(run.result, run.resultKind, configStep);
      return emptyState === null || emptyRun === null ? null : (
        <EmptyResultState
          copy={emptyState}
          run={emptyRun}
          {...(onEditConfig === undefined ? {} : { onEdit: onEditConfig })}
        />
      );
    }
    if (isCreativeScoresResult(run.result)) {
      const renderScores = (onOpen?: (row: ResultRow) => void): ReactNode => (
        <CreativeScoresView
          runId={run.runId}
          result={run.result}
          rows={rows}
          {...(selection === undefined ? {} : { selection })}
          {...(onOpen === undefined ? {} : { onOpen })}
        />
      );
      return creativeDetail === undefined ? (
        renderScores()
      ) : (
        <CreativeDetailOpener
          workflowId={run.actionType}
          rows={rows}
          creativeDetail={creativeDetail}
          tab="scores"
        >
          {renderScores}
        </CreativeDetailOpener>
      );
    }
    const renderDetail = isIterationsResult(run.result)
      ? (row: ResultRow) => iterationsDetail(row, picks, collapsibleWhy)
      : undefined;
    const grouped = groupedRowsOf(rows);
    const renderRows = (onOpen?: (row: ResultRow) => void): ReactNode => (
      <CreativeRows
        rows={grouped ?? rows}
        columns={columnsOf(rows)}
        {...(grouped === null ? {} : { groupOf: groupLabelOf })}
        {...(selection === undefined ? {} : { selection })}
        {...(renderDetail === undefined ? {} : { renderDetail })}
        {...(onOpen === undefined ? {} : { onOpen })}
      />
    );
    return creativeDetail === undefined ? (
      renderRows()
    ) : (
      <CreativeDetailOpener workflowId={run.actionType} rows={rows} creativeDetail={creativeDetail}>
        {renderRows}
      </CreativeDetailOpener>
    );
  }

  // The step confirms the brief and hands it over; the brief itself is read in
  // the drawer, so the row carries the strip rather than the whole hand-off.
  if (run.resultKind === 'brief') {
    const renderBriefs = (onOpen?: (row: ResultRow) => void): ReactNode => (
      <BriefRows
        run={run}
        renderBrief={(brief) => (
          <BriefCreatedStrip
            count={brief.recommendations.length}
            {...(onViewBrief === undefined ? {} : { onView: () => onViewBrief(brief.creativeId) })}
          />
        )}
        {...(onOpen === undefined ? {} : { onOpen })}
      />
    );
    return creativeDetail === undefined ? (
      renderBriefs()
    ) : (
      <CreativeDetailOpener
        workflowId={run.actionType}
        rows={briefed(run).map((entry) => entry.row)}
        creativeDetail={creativeDetail}
      >
        {renderBriefs}
      </CreativeDetailOpener>
    );
  }

  // The ideation scan: proven elements to pick a brief's anchors from.
  if (resultData.kind === 'element_pool') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="error">These proven elements could not be read.</Alert>;
    if (parsed.data.elements.length === 0)
      return <Alert severity="info">No proven elements were found.</Alert>;
    return (
      <ProvenElementsView pool={parsed.data} {...(selection === undefined ? {} : { selection })} />
    );
  }

  // Elements, not creatives: grouped by the category the service ranked them in.
  if (resultData.kind === 'element_list') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="error">These hidden gems could not be read.</Alert>;
    const groups = gemGroupsOf(parsed.data);
    if (groups.length === 0) return null;
    const keys = gemKeysOf(groups);
    // The brief the scan wrote from its gems; the gem picks, not this card, change it.
    const written = ideationResultSchema.safeParse(run.result);
    const briefs = written.success ? written.data.briefs : [];
    const briefRole = run.labels?.['brief_role'];

    return (
      <Stack sx={{ gap: 4 }} data-testid="gem-groups">
        <SelectAllHeader picks={gemPicks} keys={keys}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            {keys.length} {keys.length === 1 ? 'HIDDEN GEM' : 'HIDDEN GEMS'} FOUND
          </Typography>
        </SelectAllHeader>
        {groups.map((group) => (
          <GemGroupSection
            key={group.category}
            group={group}
            bestTooltip={run.labels?.['best_uplift_tip'] ?? null}
            {...(gemPicks === undefined ? {} : { picks: gemPicks })}
          />
        ))}
        {briefs.map((brief) => (
          <Stack key={brief.name} sx={{ gap: 2 }} data-testid="gems-brief">
            {briefRole === undefined ? null : (
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>
                {briefRole.toUpperCase()}
              </Typography>
            )}
            <IdeationBriefCard brief={brief} />
          </Stack>
        ))}
      </Stack>
    );
  }

  if (resultData.kind === 'rising_elements') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="error">These rising elements could not be read.</Alert>;
    const groups = risingGroupsOf(parsed.data);
    if (groups.length === 0) return null;
    const keys = groups.flatMap((group) => group.elements.map((element) => element.key));
    return (
      <Stack sx={{ gap: 4 }}>
        <SelectAllHeader picks={gemPicks} keys={keys}>
          <Box />
        </SelectAllHeader>
        <RisingElementsView
          groups={groups}
          {...(gemPicks === undefined ? {} : { picks: gemPicks })}
        />
      </Stack>
    );
  }

  if (resultData.kind === 'combination_list') {
    const { parsed } = resultData;
    if (!parsed.success)
      return <Alert severity="error">These combinations could not be read.</Alert>;
    const combos = combosOf(parsed.data);
    if (combos.length === 0) return null;
    const keys = comboKeysOf(combos);
    const elementCounts = elementCountsOf(combos);
    const countLabel = parsed.data.count_label ?? null;
    const hasScenes = combos.some((combo) =>
      combo.elements.some((element) => element.scene !== null),
    );
    const renderCombos = (onOpen?: (row: ResultRow) => void): ReactNode => (
      <Stack sx={{ gap: 2 }} data-testid="combo-list">
        <SelectAllHeader picks={gemPicks} keys={keys}>
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {hasScenes ? <SceneLegend /> : null}
            {countLabel === null ? null : (
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'primary.main' }}>
                {countLabel.toUpperCase()}
              </Typography>
            )}
          </Stack>
        </SelectAllHeader>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          {combos.map((combo) => (
            <ComboCard
              key={combo.key}
              combo={combo}
              elementCounts={elementCounts}
              {...(gemPicks === undefined ? {} : { picks: gemPicks })}
              {...(onOpen === undefined ? {} : { onOpen })}
            />
          ))}
        </Box>
      </Stack>
    );
    return creativeDetail === undefined ? (
      renderCombos()
    ) : (
      <CreativeDetailOpener
        workflowId={run.actionType}
        rows={combos.flatMap((combo) => combo.creatives)}
        creativeDetail={creativeDetail}
      >
        {renderCombos}
      </CreativeDetailOpener>
    );
  }

  if (run.resultKind === 'scored_batch') {
    return (
      <ScoredBatchView
        result={run.result}
        runId={run.runId}
        completedAt={run.completedAt}
        {...(onOpenFullAnalysis === undefined ? {} : { onOpenFullAnalysis })}
        {...(onOpenCreative === undefined ? {} : { onOpenCreative })}
      />
    );
  }

  if (run.resultKind === 'report') {
    const report = parseReport(run.result);
    if (report !== null) return <ReportView result={report} labels={run.labels ?? {}} />;
    const summary = summaryOf(run.result);
    return summary === null ? null : (
      <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{summary}</Typography>
    );
  }

  if (run.resultKind === 'ask_evo') {
    const askEvo = parseAskEvo(run.result);
    if (askEvo === null) return null;
    return <AskEvoAnswerView result={askEvo} />;
  }

  if (run.resultKind === null) return null;
  return (
    <Alert severity="info">
      This run returned a {run.resultKind} result, which has no view yet.
    </Alert>
  );
}
